import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, resolveComponent as _resolveComponent, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "text-h2 q-px-lg q-py-sm bg-tooltip-and-input-back" }
const _hoisted_2 = {
  key: 0,
  class: "q-mr-sm text-weight-regular"
}
const _hoisted_3 = { class: "text-bold" }
const _hoisted_4 = { class: "q-pl-lg" }
const _hoisted_5 = { class: "row items-center q-pb-md q-pt-sm" }
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = { class: "flex gap-md col-3" }

import { sanitize } from "dompurify"
import { ComponentConstructor, useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxBreadcrumbs from "src/components/navigation/SfxBreadcrumbs.vue"
import SfxTopNavigationManager from "src/components/navigation/SfxTopNavigationManager.vue"
import SfxTableToggle from "src/components/table/SfxTableToggle.vue"
import SfxWorkOrderIssueItemAssignment from "src/pages/manager/overlay/SfxWorkOrderIssueItemAssignment.vue"
import SfxWorkOrderItemAssignment from "src/pages/manager/overlay/SfxWorkOrderItemAssignment.vue"
import { ContextMenuItem } from "src/util/classes/contextMenuItem"
import { MANAGER_MAIN_MENU } from "src/util/constants/configurations"
import { ContextAction } from "src/util/enums/entityType"
import { NavigationAction } from "src/util/enums/topNavigation"
import { confirmWorkOrderEntitiesModification, showFeedback } from "src/util/functions/dialogs"
import { helpTourNotImplemented } from "src/util/functions/helpTour"
import { getWorkDomainWorkspaceData } from "src/util/functions/workDomain"
import { Breadcrumbs } from "src/util/types/components/sfxBreadcrumb"
import { ProgressIndicatorState } from "src/util/types/components/sfxProgressIndicator"
import { EntityPages } from "src/util/types/dataModels/entityDefinition"
import { Issue } from "src/util/types/dataModels/issue"
import { Item } from "src/util/types/dataModels/item"
import { WorkDomainWorkspace } from "src/util/types/dataModels/workDomain"
import { WorkOrderCorrectiveItem, WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"
import { computed, onBeforeMount, PropType, ref } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxWorkOrderEntityAssignment',
  props: {
  workOrder: {
    type: Object as PropType<WorkOrderRegularItem | WorkOrderCorrectiveItem>,
    required: true,
  },

  /**
   * If the progressState is 'closed' or 'complete_closed' only show `entitiesPerformed`.
   */
  progressState: {
    type: String as PropType<ProgressIndicatorState>,
    required: true,
  },

  entitiesIncluded: {
    type: Array as PropType<Item[] | Issue[]>,
    required: true,
  },

  entitiesPerformed: {
    type: Array as PropType<Item[] | Issue[]>,
    required: true,
  },

  entity: {
    type: String as PropType<EntityPages>,
    required: true,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { t } = useI18n()
const { dialogRef, onDialogOK, onDialogCancel } = useDialogPluginComponent()

const menuContent = MANAGER_MAIN_MENU

const title = computed<string>(() => props.workOrder?.designation)
const titleReference = computed<string | null>(() => props.workOrder?.reference)
const breadcrumbs = computed<Breadcrumbs>(
  () => workOrderEntityAssignmentRef.value?.breadcrumbs || []
)

const topNavigationContainer = ref<HTMLDivElement | null>(null)

const contentStyle = computed(() => {
  const topNavigationHeight = topNavigationContainer.value?.offsetHeight || 0
  return { height: `calc(100% - ${topNavigationHeight}px)` }
})

const quickActions = [ContextAction.HELP, ContextAction.FEEDBACK]

const btnResetLabel = t("sfxButtons.btn_reset_selection")
const btnSaveLabel = t("sfxButtons.btn_save_selection")
const isDisplayingToDoEntities = ref(props.progressState !== "complete_closed")
const isReadOnly = ref(["complete_closed", "closed"].includes(props.progressState))

const workDomain = ref<WorkDomainWorkspace>()
const restrictedLabel = computed<string>(() =>
  t("workOrder.restricted", { designation: sanitize(workDomain.value?.designation || "") })
)

const performedCount = computed(() => props.entitiesPerformed?.length)

const initialSelectIds = computed<string[]>(
  () => workOrderEntityAssignmentRef.value?.initialSelectIds || []
)
const selectIds = computed<string[]>(() => workOrderEntityAssignmentRef.value?.selectIds || [])
const selectionSame = computed<boolean>(
  () => initialSelectIds.value.toString() === selectIds.value.toString()
)

const workOrderEntityComponent = computed(() => {
  let component: ComponentConstructor | undefined

  if (props.entity === "item") {
    component = SfxWorkOrderItemAssignment
  } else if (props.entity === "issue") {
    component = SfxWorkOrderIssueItemAssignment
  }

  return component
})

const workOrderEntityAssignmentRef = ref<typeof SfxWorkOrderItemAssignment>()

//endregion

//region Lifecycle

onBeforeMount(async () => {
  workDomain.value = await getWorkDomainWorkspaceData(props.workOrder?.work_domain)
})

//endregion

//region Functions

async function handleContextSelect(contextMenuItem: ContextMenuItem) {
  if (contextMenuItem.action === ContextAction.HELP) {
    return helpTourNotImplemented("WorkOrderEntityAssignment")
  } else if (contextMenuItem.action === ContextAction.FEEDBACK) {
    return showFeedback()
  }
}

//endregion

//region Setup Code

function resetSelection() {
  workOrderEntityAssignmentRef.value?.resetSelection()
}

async function saveSelection() {
  const entitiesRemoved = initialSelectIds.value.filter((i) => !selectIds.value.includes(i)).length
  const entitiesAdded = selectIds.value.filter((i) => !initialSelectIds.value.includes(i)).length
  const entitiesToDo = selectIds.value.length
  const totalEntities = selectIds.value.length + performedCount.value

  const userConfirmed = await confirmWorkOrderEntitiesModification(
    entitiesRemoved,
    entitiesAdded,
    entitiesToDo,
    totalEntities,
    props.entity
  )

  if (userConfirmed) {
    const totalEntitiesIds = selectIds.value.concat(props.entitiesPerformed.map((i) => i.id))
    onDialogOK(totalEntitiesIds)
  }
}

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    "transition-show": "slide-up",
    "transition-hide": "slide-down",
    maximized: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            ref_key: "topNavigationContainer",
            ref: topNavigationContainer
          }, [
            _createVNode(SfxTopNavigationManager, {
              "main-menu-content": _unref(menuContent),
              "disable-extended-menu": "",
              "disable-main-menu": "",
              "disable-search-bar": "",
              "disable-context-menu": "",
              "disable-notifications": "",
              "disable-todo-icon": "",
              "disable-user-display": "",
              onContextSelect: handleContextSelect,
              "quick-actions": quickActions
            }, null, 8, ["main-menu-content"]),
            _createElementVNode("div", _hoisted_1, [
              (titleReference.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(titleReference.value), 1))
                : _createCommentVNode("", true),
              _createElementVNode("span", _hoisted_3, _toDisplayString(title.value), 1)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(SfxBreadcrumbs, {
                breadcrumbs: breadcrumbs.value,
                "navigation-action": _unref(NavigationAction).CLOSE,
                "is-dialog": "",
                onClose: _cache[0] || (_cache[0] = ($event: any) => (_unref(onDialogCancel)())),
                class: "bg-tooltip-and-input-back"
              }, null, 8, ["breadcrumbs", "navigation-action"])
            ])
          ], 512),
          _createVNode(_component_q_card_section, {
            class: "bg-tooltip-and-input-back scroll",
            style: _normalizeStyle(contentStyle.value)
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("span", {
                  class: "text-subtitle2 col-3",
                  innerHTML: restrictedLabel.value
                }, null, 8, _hoisted_6),
                _createVNode(SfxTableToggle, {
                  class: "col-6 justify-center",
                  modelValue: isDisplayingToDoEntities.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((isDisplayingToDoEntities).value = $event)),
                  disable: props.progressState === 'complete_closed',
                  entity: props.entity,
                  "to-do-count": selectIds.value.length,
                  "performed-count": performedCount.value
                }, null, 8, ["modelValue", "disable", "entity", "to-do-count", "performed-count"]),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(SfxBtnOutline, {
                    class: "col",
                    size: "16px",
                    padding: "10px",
                    disable: selectionSame.value,
                    onClick: resetSelection
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(btnResetLabel)), 1)
                    ]),
                    _: 1
                  }, 8, ["disable"]),
                  _createVNode(SfxBtn, {
                    class: "col",
                    size: "16px",
                    padding: "10px",
                    disable: selectionSame.value,
                    onClick: saveSelection
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(btnSaveLabel)), 1)
                    ]),
                    _: 1
                  }, 8, ["disable"])
                ])
              ]),
              (_openBlock(), _createBlock(_resolveDynamicComponent(workOrderEntityComponent.value), {
                ref_key: "workOrderEntityAssignmentRef",
                ref: workOrderEntityAssignmentRef,
                "work-order": props.workOrder,
                "entities-included": props.entitiesIncluded,
                "entities-performed": props.entitiesPerformed,
                "is-read-only": isReadOnly.value,
                "is-displaying-to-do-entities": isDisplayingToDoEntities.value
              }, null, 8, ["work-order", "entities-included", "entities-performed", "is-read-only", "is-displaying-to-do-entities"]))
            ]),
            _: 1
          }, 8, ["style"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})